import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const GovCartContext = createContext(null);
const STORAGE_KEY = 'gov_cart';

export const useGovCart = () => {
  const ctx = useContext(GovCartContext);
  if (!ctx) throw new Error('useGovCart must be used within GovCartProvider');
  return ctx;
};

export const GovCartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = useCallback((product, qty = 1) => {
    setItems(prev => {
      const existing = prev.find(i => i.id === product.id);
      if (existing) {
        return prev.map(i => i.id === product.id ? { ...i, quantity: i.quantity + qty } : i);
      }
      return [...prev, {
        id: product.id,
        name: product.name,
        sku: product.sku,
        price: product.contract_price || product.price,
        image: product.image,
        manufacturer: product.manufacturer,
        uom: product.uom,
        quantity: qty,
      }];
    });
  }, []);

  const update = useCallback((id, quantity) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(i => i.id !== id));
    } else {
      setItems(prev => prev.map(i => i.id === id ? { ...i, quantity } : i));
    }
  }, []);

  const remove = useCallback((id) => setItems(prev => prev.filter(i => i.id !== id)), []);
  const clear = useCallback(() => setItems([]), []);

  const total = items.reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.quantity) || 0), 0);
  const count = items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);

  const value = useMemo(() => ({ items, add, update, remove, clear, total, count }),
    [items, add, update, remove, clear, total, count]);

  return <GovCartContext.Provider value={value}>{children}</GovCartContext.Provider>;
};
